import {
  addDaysToKey,
  dateOnlyKey,
  officeCalendarDate,
  SystemRole,
  type ChecklistItemStatus,
  type User,
} from '@peles/shared';

/** Someone a task can be assigned to. */
export interface TaskAssignee {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

export const TASK_STATUS_OPTIONS: { id: ChecklistItemStatus; label: string }[] = [
  { id: 'TODO', label: 'To do' },
  { id: 'IN_PROGRESS', label: 'In progress' },
  { id: 'BLOCKED', label: 'Blocked' },
  { id: 'DONE', label: 'Done' },
];

export const TASK_STATUS_LABELS = Object.fromEntries(
  TASK_STATUS_OPTIONS.map((o) => [o.id, o.label]),
) as Record<ChecklistItemStatus, string>;

export const TASK_STATUS_CLASSES: Record<ChecklistItemStatus, string> = {
  TODO: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-50 text-blue-700',
  BLOCKED: 'bg-red-50 text-red-700',
  DONE: 'bg-green-50 text-green-700',
};

export type DueTone = 'overdue' | 'today' | 'soon' | 'later';

export const DUE_TONE_CLASSES: Record<DueTone, string> = {
  overdue: 'bg-red-50 text-red-700',
  today: 'bg-amber-50 text-amber-800',
  soon: 'bg-primary-50 text-primary-700',
  later: 'bg-gray-100 text-gray-600',
};

function formatKey(key: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(`${key}T00:00:00Z`).toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
}

/**
 * How a due date reads next to a task: "Today", "Tomorrow", "Yesterday", a
 * weekday within the coming week, else the date — and how urgent it looks.
 */
export function describeDue(
  dueDate: Date | string,
  todayKey: string,
): { label: string; tone: DueTone } {
  const key = dateOnlyKey(dueDate);
  if (key === todayKey) return { label: 'Today', tone: 'today' };
  if (key === addDaysToKey(todayKey, 1)) return { label: 'Tomorrow', tone: 'soon' };
  if (key === addDaysToKey(todayKey, -1)) return { label: 'Yesterday', tone: 'overdue' };
  const sameYear = key.slice(0, 4) === todayKey.slice(0, 4);
  const date = formatKey(key, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  if (key < todayKey) return { label: date, tone: 'overdue' };
  if (key < addDaysToKey(todayKey, 7)) {
    return {
      label: formatKey(key, { weekday: 'short', month: 'short', day: 'numeric' }),
      tone: 'soon',
    };
  }
  return { label: date, tone: 'later' };
}

export interface QuickDate {
  id: string;
  label: string;
  /** YYYY-MM-DD */
  value: string;
}

/**
 * One-tap due dates: today, tomorrow, a week out — and, when the task belongs
 * to an event, the week before it and the day itself (the office's calendar
 * day, the same one template dates count from).
 */
export function quickDates(todayKey: string, eventStartAt?: Date | string | null): QuickDate[] {
  const dates: QuickDate[] = [
    { id: 'today', label: 'Today', value: todayKey },
    { id: 'tomorrow', label: 'Tomorrow', value: addDaysToKey(todayKey, 1) },
    { id: 'week', label: 'In a week', value: addDaysToKey(todayKey, 7) },
  ];
  if (eventStartAt) {
    const eventDay = dateOnlyKey(officeCalendarDate(new Date(eventStartAt)));
    const weekBefore = addDaysToKey(eventDay, -7);
    if (weekBefore >= todayKey) {
      dates.push({ id: 'event-week', label: 'Week before event', value: weekBefore });
    }
    if (eventDay >= todayKey) dates.push({ id: 'event-day', label: 'Event day', value: eventDay });
  }
  return dates;
}

/** Initials for a compact assignee chip. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * Who tasks can be assigned to: the office (administrators and planners),
 * the event's clients who manage their own tasks in the portal (`clients`,
 * named as clients, after the office), plus anyone already assigned, so an
 * existing assignment never shows blank.
 */
export function taskAssignees(
  users: User[],
  alsoInclude: (string | null | undefined)[] = [],
  clients: { id: string; name: string }[] = [],
): TaskAssignee[] {
  const keep = new Set(alsoInclude.filter(Boolean));
  const clientIds = new Set(clients.map((c) => c.id));
  const byName = (a: TaskAssignee, b: TaskAssignee) => a.name.localeCompare(b.name);
  const office = users
    .filter(
      (u) =>
        !clientIds.has(u.id) &&
        (u.systemRole === SystemRole.ADMIN ||
          u.systemRole === SystemRole.PLANNER ||
          keep.has(u.id)),
    )
    .map((u) => ({ id: u.id, name: u.name, avatarUrl: u.avatarUrl }))
    .sort(byName);
  const eventClients = clients.map((c) => ({ id: c.id, name: `${c.name} · Client` })).sort(byName);
  return [...office, ...eventClients];
}
